/**
 * AdminJobWorkbench — one dialog to fully prepare a job for invoicing.
 * Admins: everything. Supervisors: scoped server-side to their teams.
 *
 * Tabs:
 *  - Assignment: one worker / ad-hoc crew (multiple workers, no team
 *    needed) / an existing team.
 *  - Time & Crew: view, add, edit, delete work sessions for ANY worker.
 *    Adding a session can also create a payroll time entry (default ON).
 *  - Materials & Costs: materials (existing endpoints) + invoice charges
 *    (travel / fuel surcharge / tools / other — admins only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Users, Clock, Receipt, Plus, Trash2, Pencil, Loader2, Save, User as UserIcon } from 'lucide-react';
import CrewHoursPanel from './CrewHoursPanel';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const toUtcIso = (local) => (local ? new Date(local).toISOString() : null);

const CHARGE_KINDS = [
  { value: 'fuel_surcharge', label: 'Fuel surcharge' },
  { value: 'travel', label: 'Travel' },
  { value: 'tools_equipment', label: 'Tools & equipment' },
  { value: 'other', label: 'Other' },
];

export default function AdminJobWorkbench({ open, onOpenChange, job, users, teams, isAdmin, onUpdated }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  // Assignment state
  const [mode, setMode] = useState('individual');
  const [workerId, setWorkerId] = useState('');
  const [crewIds, setCrewIds] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [crewFilter, setCrewFilter] = useState('');

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [newSession, setNewSession] = useState({ worker_id: '', start: '', end: '', note: '', payroll: true });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ start: '', end: '' });

  // Materials & charges state
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({ description: '', quantity: '1', cost: '' });
  const [charges, setCharges] = useState([]);

  useEffect(() => {
    if (!open || !job) return;
    if (job.assigned_team_id) {
      setMode('team');
      setTeamId(job.assigned_team_id);
    } else if ((job.assigned_team_member_ids || []).length > 1) {
      setMode('crew');
    } else {
      setMode('individual');
    }
    setWorkerId(job.assigned_to || '');
    // Drop stale/soft-deleted ids from the snapshot so Crew mode never
    // sends phantom worker_ids the backend will reject.
    let seedCrew = job.assigned_team_member_ids?.length ? [...job.assigned_team_member_ids] : (job.assigned_to ? [job.assigned_to] : []);
    if (users.length) {
      const validIds = new Set(users.map((u) => u.id));
      seedCrew = seedCrew.filter((id) => validIds.has(id));
    }
    setCrewIds(seedCrew);
    setSessions(job.work_sessions || []);
    setTotalHours(job.total_hours_worked || 0);
    setMaterials(job.materials || []);
    setCharges((job.invoice_charges || []).map((c) => ({ ...c })));
    setNewSession({ worker_id: '', start: '', end: '', note: '', payroll: true });
    setEditingId(null);
    setCrewFilter('');
  }, [open, job]);

  const workerName = (id) => users.find((u) => u.id === id)?.name || 'Unknown';
  const filteredUsers = useMemo(
    () => users.filter((u) => !crewFilter || (u.name || '').toLowerCase().includes(crewFilter.toLowerCase())),
    [users, crewFilter]
  );

  if (!job) return null;
  const authHdr = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const fail = (e, fallback) =>
    toast({ title: 'Error', description: e?.response?.data?.detail || fallback, variant: 'destructive' });

  const saveAssignment = async () => {
    setBusy(true);
    try {
      const payload = mode === 'individual'
        ? { mode, worker_id: workerId }
        : mode === 'crew'
          ? { mode, worker_ids: crewIds }
          : { mode, team_id: teamId };
      await axios.put(`${API}/jobs/${job.id}/assignment`, payload, authHdr);
      toast({ title: 'Assignment updated', description: mode === 'crew' ? `${crewIds.length} workers on this job` : undefined });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not update assignment'); }
    setBusy(false);
  };

  const addSession = async () => {
    if (!newSession.worker_id || !newSession.start || !newSession.end) {
      toast({ title: 'Missing info', description: 'Pick a worker, start and end time', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/work-sessions`, {
        worker_id: newSession.worker_id,
        started_at: toUtcIso(newSession.start),
        ended_at: toUtcIso(newSession.end),
        note: newSession.note || null,
        create_time_entry: newSession.payroll,
      }, authHdr);
      setSessions((prev) => [...prev, res.data.session]);
      setTotalHours(res.data.total_hours_worked);
      setNewSession({ worker_id: '', start: '', end: '', note: '', payroll: true });
      toast({ title: 'Session added', description: `${res.data.session.hours_worked}h${newSession.payroll ? ' · payroll entry created' : ''}` });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not add session'); }
    setBusy(false);
  };

  const saveSessionEdit = async (sessionId) => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/jobs/${job.id}/work-sessions/${sessionId}`, {
        started_at: toUtcIso(editDraft.start),
        ended_at: toUtcIso(editDraft.end),
      }, authHdr);
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? res.data.session : s)));
      setTotalHours(res.data.total_hours_worked);
      setEditingId(null);
      toast({ title: 'Session updated', description: `${res.data.session.hours_worked}h` });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not update session'); }
    setBusy(false);
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Remove this work session? Linked payroll entry (if any) is removed too.')) return;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/jobs/${job.id}/work-sessions/${sessionId}`, authHdr);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setTotalHours(res.data.total_hours_worked);
      toast({ title: 'Session removed' });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not remove session'); }
    setBusy(false);
  };

  const addMaterial = async () => {
    if (!newMaterial.description.trim()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/materials`, {
        description: newMaterial.description,
        quantity: newMaterial.quantity || 1,
        cost: newMaterial.cost || null,
      }, authHdr);
      setMaterials((prev) => [...prev, res.data.material]);
      setNewMaterial({ description: '', quantity: '1', cost: '' });
      toast({ title: 'Material added' });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not add material'); }
    setBusy(false);
  };

  const deleteMaterial = async (mid) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/jobs/${job.id}/materials/${mid}`, authHdr);
      setMaterials((prev) => prev.filter((m) => m.id !== mid));
      onUpdated?.();
    } catch (e) { fail(e, 'Could not remove material'); }
    setBusy(false);
  };

  const saveCharges = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/jobs/${job.id}/invoice-fields`, { invoice_charges: charges }, authHdr);
      toast({ title: 'Charges saved' });
      onUpdated?.();
    } catch (e) { fail(e, 'Could not save charges'); }
    setBusy(false);
  };

  const inputCls = 'bg-[#0F1923] border-[#2D3748] text-white';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1A2433] border-[#2D3748] text-white" data-testid="job-workbench-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {job.job_number && <span className="text-xs font-mono text-[#1E88E5] bg-[#1E88E5]/10 px-2 py-0.5 rounded">{job.job_number}</span>}
            Job Workbench — {job.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Fix assignment, worked time, materials and costs — even after completion — so the job is ready for invoicing.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="assignment">
          <TabsList className="bg-[#0F1923] border border-[#2D3748]">
            <TabsTrigger value="assignment" data-testid="workbench-tab-assignment"><Users className="w-4 h-4 mr-1" />Assignment</TabsTrigger>
            <TabsTrigger value="time" data-testid="workbench-tab-time"><Clock className="w-4 h-4 mr-1" />Time & Crew</TabsTrigger>
            <TabsTrigger value="costs" data-testid="workbench-tab-costs"><Receipt className="w-4 h-4 mr-1" />Materials & Costs</TabsTrigger>
          </TabsList>

          {/* ── Assignment ─────────────────────────────────────────── */}
          <TabsContent value="assignment" className="space-y-4 pt-3">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'individual', label: 'One worker', desc: 'Single assignee' },
                { v: 'crew', label: 'Crew', desc: 'Multiple workers, no team needed' },
                { v: 'team', label: 'Team', desc: 'An existing team' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMode(opt.v)}
                  data-testid={`assignment-mode-${opt.v}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${mode === opt.v ? 'border-[#1E88E5] bg-[#1E88E5]/10' : 'border-[#2D3748] hover:border-slate-500'}`}
                >
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-slate-400">{opt.desc}</div>
                </button>
              ))}
            </div>

            {mode === 'individual' && (
              <div>
                <Label>Worker</Label>
                <Select value={workerId} onValueChange={setWorkerId}>
                  <SelectTrigger className={inputCls} data-testid="assignment-worker-select"><SelectValue placeholder="Pick a worker" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {mode === 'crew' && (
              <div className="space-y-2">
                <Label>Crew members ({crewIds.length} selected)</Label>
                <Input placeholder="Search workers…" value={crewFilter} onChange={(e) => setCrewFilter(e.target.value)} className={inputCls} data-testid="crew-search-input" />
                <div className="max-h-52 overflow-y-auto border border-[#2D3748] rounded-lg divide-y divide-[#2D3748]">
                  {filteredUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#0F1923]" data-testid={`crew-member-row-${u.id}`}>
                      <Checkbox
                        checked={crewIds.includes(u.id)}
                        onCheckedChange={(v) => setCrewIds((prev) => (v ? [...prev, u.id] : prev.filter((x) => x !== u.id)))}
                      />
                      <UserIcon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm">{u.name}</span>
                      <span className="text-xs text-slate-500 ml-auto">{u.role}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {mode === 'team' && (
              <div>
                <Label>Team</Label>
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger className={inputCls} data-testid="assignment-team-select"><SelectValue placeholder="Pick a team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => {
                      const parent = t.parent_team_id ? teams.find((p) => p.id === t.parent_team_id) : null;
                      return <SelectItem key={t.id} value={t.id}>{parent ? `${parent.name} › ${t.name}` : t.name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              onClick={saveAssignment}
              disabled={busy || (mode === 'individual' && !workerId) || (mode === 'crew' && crewIds.length === 0) || (mode === 'team' && !teamId)}
              className="bg-[#1E88E5] hover:bg-[#1976D2]"
              data-testid="save-assignment-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Assignment
            </Button>
          </TabsContent>

          {/* ── Time & Crew ────────────────────────────────────────── */}
          <TabsContent value="time" className="space-y-4 pt-3">
            <CrewHoursPanel jobId={job.id} refreshKey={`${sessions.length}-${totalHours}`} />
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-sm">Work sessions</h4>
              <span className="text-sm text-emerald-400 font-semibold" data-testid="workbench-total-hours">{Number(totalHours || 0).toFixed(2)}h total</span>
            </div>
            <div className="border border-[#2D3748] rounded-lg divide-y divide-[#2D3748]">
              {sessions.length === 0 && <div className="p-4 text-sm text-slate-500">No work sessions yet — add one below.</div>}
              {sessions.map((s) => (
                <div key={s.id} className="p-3 text-sm" data-testid={`session-row-${s.id}`}>
                  {editingId === s.id ? (
                    <div className="space-y-2">
                      <div className="font-medium">{s.worker_name}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Start</Label>
                          <Input type="datetime-local" value={editDraft.start} onChange={(e) => setEditDraft((d) => ({ ...d, start: e.target.value }))} className={inputCls} data-testid="session-edit-start" />
                        </div>
                        <div>
                          <Label className="text-xs">End</Label>
                          <Input type="datetime-local" value={editDraft.end} onChange={(e) => setEditDraft((d) => ({ ...d, end: e.target.value }))} className={inputCls} data-testid="session-edit-end" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveSessionEdit(s.id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="session-edit-save">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-[#2D3748]">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium">{s.worker_name}</span>
                      {s.source === 'manual' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">manual</span>}
                      <span className="text-slate-400">
                        {s.started_at ? new Date(s.started_at).toLocaleString() : '—'} → {s.ended_at ? new Date(s.ended_at).toLocaleString() : 'open'}
                      </span>
                      <span className="text-emerald-400 font-semibold">{Number(s.hours_worked || 0).toFixed(2)}h</span>
                      <span className="ml-auto flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingId(s.id); setEditDraft({ start: toLocalInput(s.started_at), end: toLocalInput(s.ended_at) }); }} className="h-7 px-2 text-slate-300" data-testid={`session-edit-btn-${s.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteSession(s.id)} className="h-7 px-2 text-red-400" data-testid={`session-delete-btn-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border border-[#2D3748] rounded-lg p-3 space-y-3 bg-[#0F1923]">
              <h4 className="font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" />Add work session</h4>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Worker</Label>
                  <Select value={newSession.worker_id} onValueChange={(v) => setNewSession((s) => ({ ...s, worker_id: v }))}>
                    <SelectTrigger className="bg-[#1A2433] border-[#2D3748]" data-testid="new-session-worker"><SelectValue placeholder="Worker" /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="datetime-local" value={newSession.start} onChange={(e) => setNewSession((s) => ({ ...s, start: e.target.value }))} className="bg-[#1A2433] border-[#2D3748]" data-testid="new-session-start" />
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="datetime-local" value={newSession.end} onChange={(e) => setNewSession((s) => ({ ...s, end: e.target.value }))} className="bg-[#1A2433] border-[#2D3748]" data-testid="new-session-end" />
                </div>
              </div>
              <Input placeholder="Note (optional)" value={newSession.note} onChange={(e) => setNewSession((s) => ({ ...s, note: e.target.value }))} className="bg-[#1A2433] border-[#2D3748]" />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={newSession.payroll} onCheckedChange={(v) => setNewSession((s) => ({ ...s, payroll: !!v }))} data-testid="new-session-payroll-checkbox" />
                Also create a payroll time entry (worker gets paid for these hours)
              </label>
              <Button onClick={addSession} disabled={busy} className="bg-[#1E88E5] hover:bg-[#1976D2]" data-testid="add-session-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Add Session
              </Button>
            </div>
          </TabsContent>

          {/* ── Materials & Costs ──────────────────────────────────── */}
          <TabsContent value="costs" className="space-y-4 pt-3">
            <div>
              <h4 className="font-semibold text-sm mb-2">Materials</h4>
              <div className="border border-[#2D3748] rounded-lg divide-y divide-[#2D3748]">
                {materials.length === 0 && <div className="p-3 text-sm text-slate-500">No materials yet.</div>}
                {materials.map((m) => (
                  <div key={m.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`material-row-${m.id}`}>
                    <span>{m.description}</span>
                    <span className="text-slate-400">× {m.quantity}</span>
                    {m.cost != null && <span className="text-emerald-400">${Number(m.cost).toFixed(2)}</span>}
                    <Button size="sm" variant="ghost" onClick={() => deleteMaterial(m.id)} className="h-7 px-2 text-red-400 ml-auto" data-testid={`material-delete-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 mt-2">
                <Input placeholder="Description (e.g. Fuel — 40L diesel)" value={newMaterial.description} onChange={(e) => setNewMaterial((m) => ({ ...m, description: e.target.value }))} className={inputCls} data-testid="new-material-desc" />
                <Input type="number" placeholder="Qty" value={newMaterial.quantity} onChange={(e) => setNewMaterial((m) => ({ ...m, quantity: e.target.value }))} className={inputCls} />
                <Input type="number" placeholder="Cost $" value={newMaterial.cost} onChange={(e) => setNewMaterial((m) => ({ ...m, cost: e.target.value }))} className={inputCls} data-testid="new-material-cost" />
                <Button onClick={addMaterial} disabled={busy || !newMaterial.description.trim()} className="bg-[#1E88E5] hover:bg-[#1976D2]" data-testid="add-material-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>

            {isAdmin ? (
              <div>
                <h4 className="font-semibold text-sm mb-2">Invoice charges (fuel, travel, equipment)</h4>
                <div className="space-y-2">
                  {charges.map((c, i) => (
                    <div key={c.id || i} className="grid grid-cols-[1.2fr_2fr_0.7fr_1fr_auto] gap-2" data-testid={`charge-row-${i}`}>
                      <Select value={c.kind || 'other'} onValueChange={(v) => setCharges((prev) => prev.map((x, j) => (j === i ? { ...x, kind: v } : x)))}>
                        <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CHARGE_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input placeholder="Description" value={c.description || ''} onChange={(e) => setCharges((prev) => prev.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} className={inputCls} />
                      <Input type="number" placeholder="Qty" value={c.quantity ?? 1} onChange={(e) => setCharges((prev) => prev.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} className={inputCls} />
                      <Input type="number" placeholder="Unit $" value={c.unit_price ?? ''} onChange={(e) => setCharges((prev) => prev.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)))} className={inputCls} />
                      <Button size="sm" variant="ghost" onClick={() => setCharges((prev) => prev.filter((_, j) => j !== i))} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" onClick={() => setCharges((prev) => [...prev, { kind: 'fuel_surcharge', description: 'Fuel surcharge', quantity: 1, unit_price: 0 }])} className="border-[#2D3748] text-slate-300" data-testid="add-charge-btn">
                    <Plus className="w-4 h-4 mr-1" />Add charge
                  </Button>
                  <Button onClick={saveCharges} disabled={busy} className="bg-[#1E88E5] hover:bg-[#1976D2]" data-testid="save-charges-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save charges
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Invoice charges (fuel/travel) can be edited by administrators.</p>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
